<script lang="ts">
	import ChevronDown from '@lucide/svelte/icons/chevron-down'
	import PanelLeftOpen from '@lucide/svelte/icons/panel-left-open'
	import PanelRightOpen from '@lucide/svelte/icons/panel-right-open'
	import Search from '@lucide/svelte/icons/search'
	import { shortcut } from '@svelte-put/shortcut'
	import { Collapsible } from 'bits-ui'
	import { debounce } from 'es-toolkit/function'

	import { resolve } from '$app/paths'
	import { page } from '$app/state'
	import ChainBadge from '#lib/components/chain-badge.svelte'
	import SearchInput from '#lib/components/search-input.svelte'
	import Badge from '#lib/components/ui/badge/badge.svelte'
	import * as Kbd from '#lib/components/ui/kbd/index.js'
	import * as Sidebar from '#lib/components/ui/sidebar/index.js'
	import {
		handleMenuClick,
		isMenuInternal,
		isSectionRegular,
		MENU_SECTIONS,
	} from '#lib/constants/menuItems.js'
	import { m } from '#lib/paraglide/messages.js'
	import { updateSearchDialog } from '#lib/utils/dialogs.svelte.js'
	import { phg } from '#lib/utils/posthog.js'

	let search = $state('')

	const sidebar = Sidebar.useSidebar()

	let chainsOpen = $state(false)
</script>

<svelte:window
	use:shortcut={{
		trigger: {
			key: 'k',
			modifier: ['ctrl', 'meta'],
			callback: () => updateSearchDialog(true),
		},
	}}
/>

<Sidebar.Root collapsible="icon">
	<Sidebar.Header>
		<Sidebar.Menu>
			<Sidebar.MenuItem>
				<Sidebar.MenuButton class="data-[slot=sidebar-menu-button]:p-1.5!">
					{#snippet child({ props })}
						<a {...props} href={resolve('/(marketing)')}>
							<img
								class="size-5"
								alt="Chainspect icon"
								crossorigin="anonymous"
								decoding="async"
								fetchpriority="high"
								height="20"
								loading="eager"
								src="https://res.cloudinary.com/dcruwhddg/image/upload/brand/icon-stroke.svg"
								width="20"
							/>

							<span class="text-branding-gradient font-bold">Chainspect</span>
						</a>
					{/snippet}
				</Sidebar.MenuButton>
			</Sidebar.MenuItem>
		</Sidebar.Menu>
	</Sidebar.Header>

	<Sidebar.Content>
		<Sidebar.Group>
			<Sidebar.GroupContent>
				<Sidebar.Menu>
					<Sidebar.MenuItem>
						<Sidebar.MenuButton onclick={() => updateSearchDialog(true)}>
							<Search />

							<span class="whitespace-nowrap">Search</span>

							<div class="ml-auto flex items-center justify-start gap-1">
								<Kbd.Group>
									<Kbd.Root>
										{#if /\b(Macintosh|Mac OS X|Mac OS|iPhone|iPad|iPod)\b/i.test(navigator.userAgent)}
											⌘
										{:else}
											Ctrl
										{/if}
									</Kbd.Root>

									<Kbd.Root>K</Kbd.Root>
								</Kbd.Group>
							</div>
						</Sidebar.MenuButton>
					</Sidebar.MenuItem>
				</Sidebar.Menu>
			</Sidebar.GroupContent>
		</Sidebar.Group>

		{#each MENU_SECTIONS as section (section.text)}
			<Sidebar.Group>
				{#if isSectionRegular(section)}
					<Sidebar.GroupLabel>
						{section.text}

						{#if section.new}
							<Badge class="ml-1 font-bold uppercase" size="4xs" variant="brand">New</Badge>
						{/if}
					</Sidebar.GroupLabel>
				{/if}

				<Sidebar.GroupContent>
					<Sidebar.Menu>
						{#if isSectionRegular(section)}
							{#each section.items as item (item.text)}
								<Sidebar.MenuItem>
									<Sidebar.MenuButton
										isActive={page.url.pathname ===
											(isMenuInternal(item) ? resolve(item.route) : item.href)}
									>
										{#snippet child({ props })}
											<a
												{...props}
												onclick={e =>
													handleMenuClick(e, section, item, page.url.pathname, 'sidebar')}
												{...isMenuInternal(item)
													? { href: resolve(item.route) }
													: {
															href: item.href,
															rel: 'external noopener noreferrer',
															target: '_blank',
														}}
											>
												<item.Icon />

												{item.text}

												{#if item.new}
													<Badge class="font-bold uppercase" size="4xs" variant="brand">New</Badge>
												{/if}
											</a>
										{/snippet}
									</Sidebar.MenuButton>
								</Sidebar.MenuItem>
							{/each}
						{:else}
							<Collapsible.Root
								class="group/collapsible"
								onOpenChange={() => !sidebar.open && sidebar.setOpen(true)}
								bind:open={chainsOpen}
							>
								{#snippet child({ props })}
									<Sidebar.MenuItem {...props}>
										<Collapsible.Trigger>
											{#snippet child({ props })}
												<Sidebar.MenuButton {...props}>
													<section.Icon />

													{section.text}

													<ChevronDown
														class="transition-transform group-data-[state=open]/collapsible:rotate-180"
													/>
												</Sidebar.MenuButton>
											{/snippet}
										</Collapsible.Trigger>

										<Collapsible.Content>
											<SearchInput
												class="mx-1 my-2"
												onkeyup={debounce(
													() =>
														search.trim() !== '' &&
														phg.capture('search_chains', { value: search.trim() }),
													1000
												)}
												placeholder={m.search_networks()}
												bind:value={search}
											/>

											<Sidebar.GroupContent>
												<Sidebar.Menu>
													{#each section.chains.filter(c => c.name
															.toLowerCase()
															.includes(search.toLowerCase())) as chain (chain.id)}
														<Sidebar.MenuItem>
															<Sidebar.MenuButton
																isActive={page.url.pathname ===
																	resolve('/(app)/chain/[slug]', { slug: chain.slug })}
															>
																{#snippet child({ props })}
																	<a
																		href={resolve('/(app)/chain/[slug]', { slug: chain.slug })}
																		{...props}
																	>
																		<img
																			class="size-5"
																			alt="{chain.name} logo"
																			crossorigin="anonymous"
																			decoding="async"
																			fetchpriority="low"
																			height="16"
																			loading="lazy"
																			src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{chain.slug}.svg"
																			width="16"
																		/>

																		{chain.name}

																		<ChainBadge {chain} />
																	</a>
																{/snippet}
															</Sidebar.MenuButton>
														</Sidebar.MenuItem>
													{/each}
												</Sidebar.Menu>
											</Sidebar.GroupContent>
										</Collapsible.Content>
									</Sidebar.MenuItem>
								{/snippet}
							</Collapsible.Root>
						{/if}
					</Sidebar.Menu>
				</Sidebar.GroupContent>
			</Sidebar.Group>
		{/each}
	</Sidebar.Content>

	<Sidebar.Footer>
		<Sidebar.Menu>
			<Sidebar.MenuItem>
				<Sidebar.MenuButton
					class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
					aria-label={sidebar.open ? 'Collapse menu' : 'Expand menu'}
					data-sidebar="trigger"
					data-slot="sidebar-trigger"
					onclick={() => {
						sidebar.toggle()
						chainsOpen = false
					}}
					size="lg"
				>
					{#if sidebar.open}
						<PanelRightOpen class="size-4" />

						<span class="text-sm whitespace-nowrap">Collapse menu</span>
					{:else}
						<PanelLeftOpen class="mx-auto size-4" />
					{/if}
				</Sidebar.MenuButton>
			</Sidebar.MenuItem>
		</Sidebar.Menu>
	</Sidebar.Footer>
</Sidebar.Root>
